import { useEffect, useMemo, useRef, useState } from "react";
import { useGetLogsQuery } from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Pause, Play, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";

const LEVELS = ["", "DEBUG", "INFO", "WARNING", "ERROR", "CRITICAL"] as const;
type Level = (typeof LEVELS)[number];

export default function Logs() {
  const [tail, setTail] = useState(300);
  const [level, setLevel] = useState<Level>("");
  const [filter, setFilter] = useState("");
  const [paused, setPaused] = useState(false);
  const [follow, setFollow] = useState(true);
  const [wrap, setWrap] = useState(true);

  const { data, isLoading, error, refetch, isFetching } = useGetLogsQuery(
    { tail, level: level || undefined },
    { pollingInterval: paused ? 0 : 5_000 },
  );

  const lines = useMemo(() => {
    const all = data?.lines ?? [];
    const f = filter.trim().toLowerCase();
    if (!f) return all;
    return all.filter((l) => l.toLowerCase().includes(f));
  }, [data, filter]);

  // Auto-scroll to the bottom on new data, unless the user scrolled away.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!follow) return;
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines, follow]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    if (atBottom !== follow) setFollow(atBottom);
  };

  const jumpToBottom = () => {
    const el = scrollerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      setFollow(true);
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <div>
          <CardTitle>Server logs</CardTitle>
          <p className="text-xs text-muted mt-1">
            Tailing <code>{data?.path ?? "logs/bot.log"}</code>
            {data?.size_bytes != null && (
              <> · {(data.size_bytes / 1024).toFixed(1)} KB on disk</>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {LEVELS.map((lvl) => (
            <button
              key={lvl || "all"}
              onClick={() => setLevel(lvl)}
              className={cn(
                "px-2 h-7 rounded-md text-xs border transition-colors",
                level === lvl
                  ? "bg-bg text-fg border-border"
                  : "text-muted border-transparent hover:text-fg hover:bg-bg",
              )}
            >
              {lvl || "all"}
            </button>
          ))}
          <Input
            type="number"
            value={String(tail)}
            min={50}
            max={5000}
            step={50}
            onChange={(e) => setTail(Math.max(50, Math.min(5000, parseInt(e.target.value || "300", 10))))}
            title="Tail size"
            className="max-w-[90px] h-8 text-xs"
          />
          <Input
            placeholder="Search…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="max-w-[200px] h-8 text-xs"
          />
          <label
            className="flex items-center gap-1 text-xs text-muted cursor-pointer select-none"
            title="Scroll to the bottom whenever new lines arrive"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-accent"
              checked={follow}
              onChange={(e) => (e.target.checked ? jumpToBottom() : setFollow(false))}
            />
            Follow
          </label>
          <label
            className="flex items-center gap-1 text-xs text-muted cursor-pointer select-none"
            title="Wrap long lines instead of scrolling horizontally"
          >
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-accent"
              checked={wrap}
              onChange={(e) => setWrap(e.target.checked)}
            />
            Wrap
          </label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPaused((p) => !p)}
            title={paused ? "Resume polling" : "Pause polling"}
          >
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            {paused ? "Resume" : "Pause"}
          </Button>
          <Button variant="ghost" size="sm" disabled={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : error ? (
          <Alert tone="danger">Failed to read logs.</Alert>
        ) : data && !data.exists ? (
          <Alert tone="info" title="Log file not found">
            Nothing has been logged yet — the rotating handler creates{" "}
            <code>logs/bot.log</code> on first write.
          </Alert>
        ) : (
          <div className="relative">
            <div
              ref={scrollerRef}
              onScroll={onScroll}
              className={cn(
                "h-[60vh] overflow-y-auto rounded-md border border-border bg-bg p-2",
                "font-mono text-xs leading-snug",
                !wrap && "overflow-x-auto",
              )}
            >
              {lines.length === 0 ? (
                <div className="text-muted">No matching lines.</div>
              ) : (
                lines.map((ln, i) => <LogLine key={i} text={ln} wrap={wrap} />)
              )}
            </div>
            {!follow && (
              <Button
                size="sm"
                variant="primary"
                className="absolute bottom-3 right-3 shadow-lg"
                onClick={jumpToBottom}
              >
                <ArrowDown className="w-3.5 h-3.5" />
                Jump to latest
              </Button>
            )}
            <div className="flex items-center justify-between text-xs text-muted">
              <span>
                Showing <b>{lines.length}</b> of {data?.lines.length ?? 0} lines
                {filter && <> · matching <code>{filter}</code></>}
              </span>
              <span>
                {paused ? (
                  <Badge tone="warn">paused</Badge>
                ) : (
                  <Badge tone={isFetching ? "info" : "good"}>polling 5s</Badge>
                )}
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LogLine({ text, wrap = true }: { text: string; wrap?: boolean }) {
  // Color the level token if we can spot it. Format from setup_logging is:
  //   "%(asctime)s [%(levelname)s] %(name)s: %(message)s"
  const m = text.match(/^\S+\s+\S+\s+\[(\w+)\]\s+/);
  const levelToken = m ? m[1] : "";
  const cls =
    levelToken === "ERROR" || levelToken === "CRITICAL"
      ? "text-bad"
      : levelToken === "WARNING"
      ? "text-warn"
      : levelToken === "DEBUG"
      ? "text-muted"
      : "";
  return (
    <div className={cn(wrap ? "whitespace-pre-wrap break-all" : "whitespace-pre", cls)}>
      {text || " "}
    </div>
  );
}
